import type { PropsWithChildren } from 'react';

import { MiniTooltip } from '#@/components/Tooltip';

import { Wrapper } from './Styles';

export interface HoverableElementProps {
  tooltipText?: string | null;

  /**
   * Set to `false` to adjust the tooltip behavior so that it does not close when the user
   * clicks on the trigger element, rationale:
   * https://app.shortcut.com/cosuno/story/43312/info-icons-should-not-be-clickable
   *
   * Set to `true` if the `children` are clickable e.g. a button, a link or a dropdown menu trigger.
   * This will allow the tooltip to be dismissed when the clickable element is clicked.
   * Otherwise, the tooltip may prevent the clickable element from being clicked.
   * This is especially true for the pattern we have on tables with "More Actions..."
   * where the button has no text but a tooltip to describe its label.
   * All tests which had to click such buttons failed because the tooltip prevents
   * clicking under it.
   */
  isTriggerElementClickable: boolean;
}

const HoverableElement = ({
  tooltipText,
  children,
  isTriggerElementClickable,
  ...props
}: PropsWithChildren<HoverableElementProps>) =>
  tooltipText ? (
    <MiniTooltip<HTMLDivElement>
      isTriggerElementClickable={isTriggerElementClickable}
      bodyText={tooltipText}
    >
      {({ ref, getReferenceProps }) => (
        <Wrapper ref={ref} {...props} {...getReferenceProps()}>
          {children}
        </Wrapper>
      )}
    </MiniTooltip>
  ) : (
    children
  );

export default HoverableElement;
